import * as React from "react";

import {
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
    toColumn,
    SimpleColumn,
} from "@impero-com/common/components/dataTable";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import {
    DataView,
    ArrayDataSource,
    ExtractorExpander,
    DataExtractor,
} from "@impero-com/common/utils/dataView";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import {
    DataViewIndex,
    SimpleAndFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification, PageSpinner } from "@impero-com/ui";
import { memoize } from "lodash-es";
import memoizeOne from "memoize-one";

import { getGetCalendarsQueryKey } from "api/generated/calendars/calendars";
import { getGetControlProgramsQueryKey } from "api/generated/control-program/control-program";
import {
    getGetControlsQueryKey,
    getGetControlsResourceCountQueryKey,
    patchControls,
} from "api/generated/controls/controls";
import { getGetFinancialYearQueryKey } from "api/generated/financial-years/financial-years";
import type { UserProfile } from "api/generated/model";
import {
    ControlWithoutFieldsGraphItemDto,
    ControlWithoutFieldsGraphItemDtoListDto as ControlWithoutFieldsListGraphDto,
    SchedulingType,
    UserOrGroup,
    ControlPolicy,
    ControlProgramOverviewResourceListDto as ControlProgramOverviewResources,
    AvailableActions,
    UserTagView,
    FinancialYearView,
    UpdatableControls,
    CalendarView,
    EntityView,
    UserTagOverviewListDto,
} from "api/generated/model";
import { getGetTagsQueryKey } from "api/generated/tags/tags";
import {
    assignmentGroupToAssignmentRows,
    buildAssignmentRowExportExtractors,
    buildAssignmentRowTagsExtractors,
    mapPropsToTableDataSource,
} from "components/controlAssignment/dataSource";
import { buildColumns } from "components/controlAssignment/table";
import {
    AssignmentRow,
    AssignmentRowForUserGroupIndividual,
    ControlAssignmentResources,
    ExportedAssignmentRow,
    ReviewerType,
} from "components/controlAssignment/types";
import { buildControlActivitiesListUrl } from "org/controlActivities/urlUtils";
import { PageStateManager } from "org/pageState/components/pageStateManager";
import { pageStateControlsCodecs } from "org/pageState/convert/controls";
import { IdAndName } from "org/pageState/models";
import { PageKeyControls, PAGE_KEY } from "org/pageState/pageKey";
import { PageFilterAndColumns, PageTypeControls } from "org/pageState/types";
import { splitPageStateWithCallback } from "org/pageState/utils";
import { controlActionToAccessRight } from "utils/actions";
import {
    ASSIGNEE_CATEGORIES_ID,
    ASSIGNEE_CATEGORY_METHODS,
    buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilter,
    buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilterInnerTable,
    buildAssigneeTagCategoryExtractorExpanderForNonGroupedTagsWithIds,
} from "utils/assigneeTags";
import {
    controlAssignmentDataToControlAssignmentGroups,
    ControlAssignmentGroup,
} from "utils/controls/assignmentUtils";
import {
    ASSIGNMENT_EXTRACTOR_KEYS,
    CTRL_EXTRACTOR_KEYS,
} from "utils/controls/extractorKeys";
import { WrappedFormUtils } from "utils/form";
import { Guid } from "utils/guid";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import {
    buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds,
    adjustFiltersWithCategoryExtractorId,
    buildCategoryExtractorsAndExpandersFromTagsAndFilter,
    DEFAULT_CATEGORY_METHODS,
    AbstractTagCategory,
} from "utils/tag";
import { isNotUndefined } from "utils/typePredicates";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { WebRoute } from "../../../../routes";
import { useNewMenu } from "../../../navigation/useNewMenu";
import { ControlResources } from "../resources";
import { ExportFormSchema } from "../schema";
import {
    ControlModuleApplicationBar,
    ControlViewPage,
    ControlViewsLayout,
    ControlViewsMain,
    PAGE_CONTROLS_ALL,
    PAGE_CONTROLS_WEEKLY,
    PAGE_CONTROLS_MONTHLY,
    PAGE_CONTROLS_TWO_MONTHS,
    PAGE_CONTROLS_QUARTERLY,
    PAGE_CONTROLS_HALF_YEARLY,
    PAGE_CONTROLS_YEARLY,
    PAGE_CONTROLS_MANUAL,
} from "../sideMenu";

import {
    ASSIGNEE_CATEGORY_EXTRACTORS_ID,
    buildInitialAssignmentNameExtractorExpander,
    buildSubsequentAssignmentNameExtractorExpander,
    buildEntityExtractorExpander,
    buildControlColumnBuilders,
    CATEGORY_EXTRACTORS_ID,
    getColumns,
    getExtractorIds,
    SchedulingTypeOrAll,
} from "./columns";
import { ChangeDetailsForm, ChangeDetailsFormValues } from "./details";
import { ExportFormContext } from "./export";
import { useCalendarsQuery } from "./queries/useCalendarsQuery";
import { useControlListQuery } from "./queries/useControlListQuery";
import { useControlProgramOverviewsQuery } from "./queries/useControlProgramOverviewsQuery";
import { useOrgFinancialYearQuery } from "./queries/useOrgFinancialYearQuery";
import { useTagOverviewsQuery } from "./queries/useTagOverviewsQuery";
import {
    ModifySchedulingForm,
    UpdatableControlSchedulingForm,
} from "./scheduling";
import * as theme from "./theme.css";
import { ControlDataRow } from "./types";

interface ControlsProps {
    schedulingTypeOrAll: SchedulingTypeOrAll;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    availableActions: AvailableActions;
}

interface ControlsState {
    status: ControlsStatus;
}

type ControlsStatus = { type: "DEFAULT" } | { type: "SUBMITTING" };

interface ControlsDataProps extends ControlsProps {
    controlEntitiesEnabled: boolean;
    newMenu: boolean;
}

interface InnerControlsProps extends ControlsDataProps {
    controls: ControlWithoutFieldsListGraphDto;
    allTags: UserTagOverviewListDto;
    orgFinancialYear: FinancialYearView | null;
    calendars: Array<CalendarView>;
    controlProgramOverviews: Partial<ControlProgramOverviewResources>;
}

function ControlsWithFeatureFlag(props: ControlsProps) {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const newMenu = useNewMenu();
    const controls = (
        <ControlsData
            {...props}
            controlEntitiesEnabled={controlEntitiesEnabled}
            newMenu={newMenu}
        />
    );

    if (!newMenu) {
        return controls;
    }

    const { page, title, breadcrumbs } = controlViewMetaData(
        props.schedulingTypeOrAll
    );
    return (
        <ControlViewsLayout page={page} title={title} breadcrumbs={breadcrumbs}>
            {controls}
        </ControlViewsLayout>
    );
}

function ControlsData(props: ControlsDataProps) {
    const {
        schedulingTypeOrAll,
        availableActions,
        controlEntitiesEnabled,
        newMenu,
    } = props;
    const controls = useControlListQuery(schedulingTypeOrAll);
    const allTags = useTagOverviewsQuery();
    const orgFinancialYear = useOrgFinancialYearQuery();
    const calendars = useCalendarsQuery();
    const controlPrograms = useControlProgramOverviewsQuery(
        controlEntitiesEnabled
    );
    const controlProgramOverviews = controlEntitiesEnabled
        ? controlPrograms.data
        : defaultControlProgramOverviews;

    if (
        controls.data &&
        allTags.data &&
        orgFinancialYear.data !== undefined &&
        calendars.data &&
        controlProgramOverviews
    ) {
        return (
            <Controls
                {...props}
                controls={controls.data}
                allTags={allTags.data}
                orgFinancialYear={orgFinancialYear.data}
                calendars={calendars.data}
                controlProgramOverviews={controlProgramOverviews}
            />
        );
    }

    const error = [
        controls,
        allTags,
        orgFinancialYear,
        calendars,
        controlPrograms,
    ].find((query) => query.error)?.error;

    if (error) {
        return (
            <ControlListError
                newMenu={newMenu}
                availableActions={availableActions}
                title={controlViewMetaData(schedulingTypeOrAll).title}
                code={error.response?.status}
            />
        );
    }

    return (
        <ControlListLoading
            newMenu={newMenu}
            availableActions={availableActions}
        />
    );
}

interface ControlListLoadingProps {
    newMenu: boolean;
    availableActions: AvailableActions;
}

function ControlListLoading({
    newMenu,
    availableActions,
}: ControlListLoadingProps) {
    if (!newMenu) {
        return <PageSpinner />;
    }

    return (
        <ControlViewsMain availableActions={availableActions}>
            <PageSpinner />
        </ControlViewsMain>
    );
}

interface ControlListErrorProps extends ControlListLoadingProps {
    title: string;
    code: number | undefined;
}

function ControlListError({
    newMenu,
    availableActions,
    title,
    code,
}: ControlListErrorProps) {
    if (!newMenu) {
        return (
            <OrgApplicationBar title={title}>
                <LoggedInHTTPError code={code} />
            </OrgApplicationBar>
        );
    }

    return (
        <ControlViewsMain availableActions={availableActions}>
            <LoggedInHTTPError code={code} />
        </ControlViewsMain>
    );
}

const defaultControlProgramOverviews: Partial<ControlProgramOverviewResources> =
    {
        content: [],
        linkedResources: {
            entities: [],
            tagCategories: [],
            tags: [],
            users: [],
        },
    };

class Controls extends React.PureComponent<InnerControlsProps, ControlsState> {
    private columnBuilders = buildControlColumnBuilders((controlId) =>
        buildControlActivitiesListUrl({ type: "control", controlId })
    );

    private asyncModifySchedulingForm = React.createRef<ModifySchedulingForm>();

    private memoizeDataView = memoizeOne(
        (
            controls: ControlWithoutFieldsListGraphDto,
            schedulingTypeOrAll: SchedulingTypeOrAll,
            orgFinancialYear: FinancialYearView | null,
            allTags: UserTagOverviewListDto,
            controlProgramOverviews: Partial<ControlProgramOverviewResources>,
            filter: SimpleAndFilter | null,
            controlEntitiesEnabled: boolean
        ): [DataView<ControlDataRow>, DataViewIndex<ControlDataRow>] => {
            const {
                linkedResources: {
                    controlPrograms,
                    tags,
                    tagCategories,
                    financialYears,
                    calendars,
                    users,
                    userGroups,
                },
            } = controls;

            const entities =
                controlProgramOverviews.linkedResources?.entities || [];
            const entitiesById = new Map(entities.map((e) => [e.id, e]));

            const usersById = new Map(
                users.map((user) => [user.resource.id, user])
            );
            const userGroupById = new Map(
                userGroups.map((userGroup) => [
                    userGroup.resource.id,
                    userGroup,
                ])
            );

            const data: Array<ControlDataRow> = controls.content.map(
                (control): ControlDataRow => {
                    const controlProgram = controlPrograms.find(
                        (cp) =>
                            cp.resource.id === control.resource.controlProgramId
                    )!;
                    const controlTags: Array<UserTagView> =
                        control.resource.tagIds.map(
                            (id) =>
                                tags.find((t) => t.resource.id === id)!.resource
                        );
                    const calendarId = fetchCalendarId(control.resource);
                    const calendar = calendars.find(
                        (cal) => cal.id === calendarId
                    )!;

                    const financialYearId = fetchFinancialYearId(
                        control.resource
                    );
                    let financialYear = null;
                    if (financialYearId) {
                        financialYear = financialYears.find(
                            (fy) => fy.id === financialYearId
                        )!;
                    }

                    const { stepGroup, assignmentSettings } = control.resource;

                    const assigneeTagIds: Set<Guid> = new Set(
                        Object.values(assignmentSettings.tagIds).flat()
                    );
                    const assigneeTags = tags
                        .filter((tag) => assigneeTagIds.has(tag.resource.id))
                        .map((tag) => tag.resource);

                    const assignments =
                        controlAssignmentDataToControlAssignmentGroups({
                            initialAssignment:
                                stepGroup.initialStep.assignments,
                            subsequentAssignments:
                                stepGroup.subsequentSteps.map(
                                    (step) => step.assignments
                                ),
                            tagIds: assignmentSettings.tagIds,
                            loginProtectionEnabled:
                                assignmentSettings.loginProtectionEnabled,
                            segregationOfDutiesEnabled:
                                assignmentSettings.segregationOfDutiesEnabled,
                            notificationRecipients:
                                stepGroup.notificationRecipients,
                            entityIds: stepGroup.entityIds,
                        });

                    return {
                        calendar,
                        control,
                        controlProgram,
                        tags: {
                            tagCategories,
                            tags: controlTags,
                        },
                        accessRight: controlActionToAccessRight(
                            control.actions
                        ),
                        financialYear,
                        assignments,
                        assigneeTags: {
                            tagCategories,
                            tags: assigneeTags,
                        },
                    };
                }
            );

            const categoryExtractorsAndExpanders =
                buildCategoryExtractorsAndExpandersFromTagsAndFilter(
                    allTags,
                    filter
                );
            const assigneeCategoryExtractorsAndExpanders =
                buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilter(
                    allTags,
                    filter
                );

            const extractorIds = [
                ...getExtractorIds(
                    schedulingTypeOrAll,
                    !!orgFinancialYear,
                    controlEntitiesEnabled
                ),
            ];
            const extractorIdLocation = extractorIds.indexOf(
                CATEGORY_EXTRACTORS_ID
            );
            if (extractorIdLocation > -1) {
                extractorIds.splice(
                    extractorIdLocation,
                    1,
                    ...categoryExtractorsAndExpanders.extractors.map(
                        (extractor) => extractor.id
                    )
                );
            }
            const assigneeExtractorIdLocation = extractorIds.indexOf(
                ASSIGNEE_CATEGORY_EXTRACTORS_ID
            );
            if (assigneeExtractorIdLocation > -1) {
                extractorIds.splice(
                    assigneeExtractorIdLocation,
                    1,
                    ...assigneeCategoryExtractorsAndExpanders.extractors.map(
                        (extractor) => extractor.id
                    )
                );
            }

            const dataView: DataView<ControlDataRow> = {
                dataSource: new ArrayDataSource(
                    data,
                    (row) => row.control.resource.id
                ),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors: getColumns(
                            schedulingTypeOrAll,
                            orgFinancialYear,
                            calendars,
                            allTags,
                            controls,
                            usersById,
                            userGroupById,
                            controlEntitiesEnabled
                        ),
                    },
                    {
                        id: "category",
                        label: "Category",
                        extractors: categoryExtractorsAndExpanders.extractors,
                    },
                    {
                        id: "assigneeCategory",
                        label: "AssigneeCategory",
                        extractors:
                            assigneeCategoryExtractorsAndExpanders.extractors,
                    },
                ],
                sorters: [{ extractorId: "name", direction: "ASC" }],
                defaultCompareItems: Cmp.map(
                    (row) => row.control.resource.id,
                    Cmp.primitive
                ),
            };
            const expanders: Array<ExtractorExpander<any, any>> = [
                ...categoryExtractorsAndExpanders.expanders,
                ...[
                    buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds(
                        allTags
                    ),
                ],
                ...[
                    buildInitialAssignmentNameExtractorExpander(
                        ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
                        "assignee",
                        usersById,
                        userGroupById
                    ),
                ],
                ...[
                    buildSubsequentAssignmentNameExtractorExpander(
                        ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
                        (group) => group.firstReviewer,
                        "firstReviewer",
                        usersById,
                        userGroupById
                    ),
                ],
                ...[
                    buildSubsequentAssignmentNameExtractorExpander(
                        ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
                        (group) => group.secondReviewer,
                        "secondReviewer",
                        usersById,
                        userGroupById
                    ),
                ],
                ...assigneeCategoryExtractorsAndExpanders.expanders,
                ...[
                    buildAssigneeTagCategoryExtractorExpanderForNonGroupedTagsWithIds(
                        allTags
                    ),
                ],
                ...(controlEntitiesEnabled
                    ? [buildEntityExtractorExpander(entitiesById)]
                    : []),
            ];
            return [
                dataView,
                new DataViewIndex(dataView, extractorIds, expanders),
            ];
        }
    );

    constructor(props: InnerControlsProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
        };
    }

    render = () => {
        const {
            schedulingTypeOrAll,
            ajaxManager,
            orgCookie,
            orgProfileCookie,
            availableActions,
            controlEntitiesEnabled,
            newMenu,
            controls,
            allTags,
            orgFinancialYear,
            calendars,
            controlProgramOverviews,
        } = this.props;
        const { page, title, breadcrumbs } =
            controlViewMetaData(schedulingTypeOrAll);
        const pageKey: PageKeyControls =
            schedulingTypeOrAllToPageKey(schedulingTypeOrAll);

        const renderError = (code: number | undefined) => (
            <ControlListError
                newMenu={newMenu}
                availableActions={availableActions}
                title={title}
                code={code}
            />
        );
        const renderLoading = () => (
            <ControlListLoading
                newMenu={newMenu}
                availableActions={availableActions}
            />
        );

        return (
            <PageStateManager<PageTypeControls, PageFilterAndColumns>
                pageKey={pageKey}
                renderLoading={renderLoading}
                renderError={renderError}
                codecs={pageStateControlsCodecs}
                renderContent={({
                    pageState,
                    onPageStateChange,
                    onCreateShareableLink,
                }) => {
                    const {
                        filter: storedFilter,
                        onFilterChange,
                        visibleColumnIds,
                        onVisibleColumnsChange,
                    } = splitPageStateWithCallback(
                        pageState,
                        onPageStateChange
                    );
                    const filter = adjustFiltersWithCategoryExtractorId(
                        allTags,
                        storedFilter
                    );
                    const [dataView, dataViewIndex] = this.memoizeDataView(
                        controls,
                        schedulingTypeOrAll,
                        orgFinancialYear,
                        allTags,
                        controlProgramOverviews,
                        filter,
                        controlEntitiesEnabled
                    );

                    const resources = (
                        <ControlResources<
                            ControlDataRow,
                            ControlAssignmentGroup,
                            ChangeDetailsFormValues,
                            object,
                            UpdatableControlSchedulingForm
                        >
                            ajaxManager={ajaxManager}
                            availableActions={availableActions}
                            orgCookie={orgCookie}
                            orgProfileCookie={orgProfileCookie}
                            dataView={dataView}
                            dataViewIndex={dataViewIndex}
                            tableMode={DATA_TABLE_MODE_VIEWPORT}
                            adjustable={true}
                            columnBuilders={this.columnBuilders}
                            renderExpandedRow={this.renderExpandedRowWithFilter(
                                filter,
                                allTags,
                                controlProgramOverviews
                            )}
                            getRowClassName={this.getRowClassName}
                            filter={filter}
                            visibleColumnIds={visibleColumnIds}
                            onFilterChanged={onFilterChange}
                            onVisibleColumnsChanged={onVisibleColumnsChange}
                            exportAction={{
                                title: t("Controls_Export_Options"),
                                onSave: this.onExport,
                            }}
                            changeDetails={{
                                canEditItem: (item) =>
                                    item.control.actions.edit,
                                readOnlyInfo: t("ChangeDetails_ReadOnlyInfo"),
                                renderDrawerBody: (
                                    selectedIds,
                                    readOnly,
                                    form
                                ) =>
                                    this.changeDetailsDrawerBody(
                                        selectedIds,
                                        readOnly,
                                        form,
                                        allTags,
                                        controls.linkedResources.controlPolicies
                                            .loginProtection
                                    ),
                                confirmationTitle: t(
                                    "Control_ChangeDetails_ConfirmationTitle"
                                ),
                                confirmationText: (selectedIds) =>
                                    t(
                                        "Control_ChangeDetails_ConfirmationText",
                                        {
                                            count: selectedIds.length,
                                        }
                                    ),
                                onSave: (_, form, done) => {
                                    this.onSubmitChanges(form, done);
                                },
                            }}
                            modifySchedule={
                                schedulingTypeOrAll !== "All"
                                    ? {
                                          canEditItem: (item) =>
                                              item.control.actions.edit,
                                          readOnlyInfo: t(
                                              "ChangeDetails_ReadOnlyInfo"
                                          ),
                                          renderDrawerBody: (
                                              selectedIds,
                                              readOnly
                                          ) =>
                                              this.modifySchedulingDrawerBody(
                                                  selectedIds,
                                                  readOnly,
                                                  allTags,
                                                  calendars,
                                                  orgFinancialYear
                                              ),
                                          confirmationTitle: t(
                                              "Control_ChangeDetails_ConfirmationTitle"
                                          ),
                                          confirmationText: (selectedIds) =>
                                              t(
                                                  "Control_ChangeDetails_ConfirmationText",
                                                  {
                                                      count: selectedIds.length,
                                                  }
                                              ),
                                          onSave: (
                                              selectedIds,
                                              _form,
                                              done
                                          ) => {
                                              this.onSubmitScheduleChanges(
                                                  selectedIds,
                                                  done
                                              );
                                          },
                                      }
                                    : undefined
                            }
                        />
                    );

                    return newMenu ? (
                        <ControlViewsMain
                            availableActions={availableActions}
                            onCreateShareableLink={onCreateShareableLink}
                        >
                            {resources}
                        </ControlViewsMain>
                    ) : (
                        <ControlModuleApplicationBar
                            title={title}
                            page={page}
                            breadcrumbs={breadcrumbs}
                            availableActions={availableActions}
                            onCreateShareableLink={onCreateShareableLink}
                        >
                            {resources}
                        </ControlModuleApplicationBar>
                    );
                }}
            />
        );
    };

    private changeDetailsDrawerBody(
        controls: ControlDataRow[],
        readOnly: boolean,
        form: WrappedFormUtils<ChangeDetailsFormValues, object>,
        allTags: UserTagOverviewListDto,
        loginProtectionPolicy: ControlPolicy
    ): React.ReactNode {
        const { ajaxManager } = this.props;
        return (
            <ChangeDetailsForm
                initialDescriptions={controls.map(
                    (c) => c.control.resource.description || ""
                )}
                initialTags={controls.map(
                    (c) => new Set(c.control.resource.tagIds)
                )}
                initialControlPrograms={controls.map(
                    (c) => c.controlProgram.resource
                )}
                initialControlNames={controls.map(
                    (c) => c.control.resource.title
                )}
                initialLoginRequired={controls.map(
                    (c) =>
                        c.control.resource.assignmentSettings
                            .loginProtectionEnabled
                )}
                loginProtectionPolicy={loginProtectionPolicy}
                controlIds={controls.map((c) => c.control.resource.id)}
                allTags={allTags}
                form={form}
                readOnly={readOnly}
                ajaxManager={ajaxManager}
            />
        );
    }

    private onExportInner<T>(
        allColumns: SimpleColumn<T>[],
        dataView: DV.DataView<T>,
        filterItem: (t: T) => boolean,
        labelTransformers: ExportLabelTransformer[]
    ) {
        const { orgCookie, orgProfileCookie } = this.props;

        if (orgProfileCookie === null) {
            throw new Error("orgProfileCookie is null");
        }

        const getLabel = (column: SimpleColumn<T>) => {
            for (const transformer of labelTransformers) {
                if (transformer.check(column.extractor.id)) {
                    return transformer.transform(column.extractor.label);
                }
            }
            return column.extractor.label;
        };

        const columns: DV.ExportColumn<T>[] = allColumns.map((column) => {
            return {
                label: getLabel(column),
                getValue: column.getCopyableValue,
            };
        });

        DV.exportToExcel<T>(
            dataView,
            filterItem,
            columns,
            "controls",
            "xlsx",
            "Controls",
            {
                date: new Date(),
                organization: {
                    name: orgCookie.name,
                },
                user: {
                    firstName: orgProfileCookie.firstName,
                    lastName: orgProfileCookie.lastName,
                    email: orgProfileCookie.email,
                },
            }
        );
    }

    private onExport = (
        selectedIds: string[],
        form: ExportFormSchema,
        ctx: ExportFormContext<ControlDataRow>,
        onDone: () => void
    ) => {
        const ignoredExtractors: string[] = [
            CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS, // Not relevant for export
            // Search only extractors are also not important
            ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
            ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
            ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
        ];

        const { includeAssignments } = form;
        const { formattedView, visibleColumnIds } = ctx;

        const dataView = formattedView.dataView;
        const formattedColumns = formattedView.columns.filter(
            (column) => !ignoredExtractors.includes(column.extractor.id)
        );

        const visibleColumns = formattedColumns.filter((column) =>
            visibleColumnIds.has(column.extractor.id)
        );

        const allColumns = [...visibleColumns];

        const labelTransformers: ExportLabelTransformer[] = [];

        const tagCategoriesIndex = allColumns.findIndex(
            (column) => column.extractor.id === CTRL_EXTRACTOR_KEYS.TAGS
        );

        if (tagCategoriesIndex >= 0) {
            const filteredDataSource = dataView.dataSource.filteredByIds(
                new Set(),
                new Set(selectedIds)
            );
            let usedCategories: Set<AbstractTagCategory> = new Set();

            DV.forEach(filteredDataSource.iter(), (row) => {
                usedCategories = new Set([
                    ...usedCategories,
                    ...row.tags.tags
                        .map((tag) => tag.tagCategoryId)
                        .map((id) =>
                            row.tags.tagCategories.find((cat) => id === cat.id)
                        )
                        .filter(isNotUndefined),
                ]);
            });

            const usedCategoriesExtractorIds = Array.from(usedCategories).map(
                DEFAULT_CATEGORY_METHODS.abstractTagCategoryToExtractorId
            );

            const tagCategoriesColumns =
                dataView.extractorGroups
                    .find((group) => group.id === "category")
                    ?.extractors.map((extractor) => toColumn(extractor)) ?? [];

            const columnsToAdd = tagCategoriesColumns
                .filter((column) =>
                    usedCategoriesExtractorIds.includes(column.extractor.id)
                )
                .sort(
                    Cmp.map((column) => column.extractor.label, Cmp.string())
                );

            allColumns.splice(tagCategoriesIndex, 1, ...columnsToAdd);

            labelTransformers.push({
                check: (extractorId) =>
                    tagCategoriesColumns
                        .map((column) => column.extractor.id)
                        .includes(extractorId),
                transform: (name) => t("Controls_Export_ControlTags", { name }),
            });
        }

        if (!includeAssignments) {
            this.onExportInner<ControlDataRow>(
                allColumns,
                dataView,
                (row) => selectedIds.includes(row.control.resource.id),
                labelTransformers
            );

            onDone();

            return;
        }

        const resources = this.props.controls.linkedResources;
        const { allTags } = this.props;
        const assignmentMap = assignmentGroupToAssignmentRows(resources);

        const { controlEntitiesEnabled } = this.props;
        const entities =
            this.props.controlProgramOverviews.linkedResources?.entities ?? [];
        const entitiesById = new Map<number, EntityView>(
            entities.map((e) => [e.id, e])
        );

        const assignmentsColumns: SimpleColumn<ExportedAssignmentRow>[] =
            buildAssignmentRowExportExtractors(
                controlEntitiesEnabled ? entitiesById : undefined
            ).map((extractor) => toColumn(extractor));

        const assignmentTagColumnsIndex = assignmentsColumns.findIndex(
            (column) =>
                column.extractor.id === ASSIGNMENT_EXTRACTOR_KEYS.EXPORT_TAGS
        );
        if (assignmentTagColumnsIndex > 0) {
            const assignmentTagsColumns: SimpleColumn<ExportedAssignmentRow>[] =
                buildAssignmentRowTagsExtractors(allTags).map((extractor) =>
                    toColumn(extractor)
                );

            const filteredDataSource = dataView.dataSource.filteredByIds(
                new Set(),
                new Set(selectedIds)
            );
            let usedCategories: Set<AbstractTagCategory> = new Set();

            DV.forEach(filteredDataSource.iter(), (row) => {
                usedCategories = new Set([
                    ...usedCategories,
                    ...row.assigneeTags.tags
                        .map((tag) => tag.tagCategoryId)
                        .map((id) =>
                            row.assigneeTags.tagCategories.find(
                                (cat) => id === cat.id
                            )
                        )
                        .filter(isNotUndefined),
                ]);
            });

            const usedCategoriesExtractorIds = Array.from(usedCategories).map(
                ASSIGNEE_CATEGORY_METHODS.abstractTagCategoryToExtractorId
            );

            const columnsToAdd: SimpleColumn<ExportedAssignmentRow>[] =
                assignmentTagsColumns
                    .filter((column) =>
                        usedCategoriesExtractorIds.includes(column.extractor.id)
                    )
                    .sort(
                        Cmp.map(
                            (column) => column.extractor.label,
                            Cmp.string()
                        )
                    );

            assignmentsColumns.splice(
                assignmentTagColumnsIndex,
                1,
                ...columnsToAdd
            );

            labelTransformers.push({
                check: (extractorId) =>
                    assignmentTagsColumns
                        .map((column) => column.extractor.id)
                        .includes(extractorId),
                transform: (name) =>
                    t("Controls_Export_Assignee_Tags", { name }),
            });
        }

        const expandedDV: DV.DataView<
            DV.Expando<ControlDataRow, AdditionalExportExpandoData>
        > = DV.expanded(dataView, [
            {
                extractorId: CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
                expand: (assignments: ControlAssignmentGroup[]) => {
                    return assignments.flatMap((assignment) => {
                        const expanded: AssignmentRow =
                            assignmentMap(assignment);

                        // We expand twice - first, each control is expanded into assignments,
                        // and then secondly, each assignment is expanded into individual member of group
                        return expandAssignmentRowIntoExportedAssignmentRows(
                            expanded
                        ).map((expandedValue) => ({
                            partialId: expandedValue.id,
                            expandedValue,
                        }));
                    });
                },
                compare: Cmp.undefinedLast((_a, _b) => 0),
                asString: (_row) => {
                    throw new Error("asString for expander has been used");
                },
            },
        ]);

        const expandedColumns: SimpleColumn<
            DV.Expando<ControlDataRow, AdditionalExportExpandoData>
        >[] = [
            ...allColumns.map(mapBaseItemExportedColumnToExpandoColumn),
            ...assignmentsColumns.map(mapAssignmentRowColumnToExpandoColumn),
        ];
        this.onExportInner<DV.Expando<ControlDataRow>>(
            expandedColumns,
            expandedDV,
            (row) => selectedIds.includes(row.baseItem.control.resource.id),
            labelTransformers
        );

        onDone();
    };

    private onSubmitChanges(
        form: WrappedFormUtils<ChangeDetailsFormValues, object>,
        onDone: (closeDrawer: boolean) => void
    ) {
        const { ajaxManager } = this.props;
        const patch: UpdatableControls | undefined =
            form.getFieldValue("patch");
        const doReload = () => {
            invalidateControlListQueries();
            onDone(true);
            emitNotification("success", t("UserManagement_ControlsUpdated"));
        };
        if (patch !== undefined) {
            ajaxManager
                .ajax<void, ControlsStatus>(() => patchControls(patch), {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    inFlightStatus: { type: "SUBMITTING" },
                    getSuccessStatus: (_resp) => ({ type: "DEFAULT" }),
                    getErrorStatus: (_code) => ({ type: "DEFAULT" }),
                })
                .then(doReload)
                .catch((e) => {
                    console.log(e);
                    onDone(false);
                });
        } else {
            throw new Error("Patch is undefined but is not supposed to be");
        }
    }

    private modifySchedulingDrawerBody(
        controls: ControlDataRow[],
        readOnly: boolean,
        allTags: UserTagOverviewListDto,
        calendars: CalendarView[],
        financialYear: FinancialYearView | null
    ): React.ReactNode {
        const { ajaxManager, schedulingTypeOrAll } = this.props;

        // Upgrade from SchedulingTypeOrAll to SchedulingType
        if (schedulingTypeOrAll === "All") {
            throw new Error(
                'schedulingTypeOrAll = "All" can not be used with ModifySchedulingForm'
            );
        }
        const schedulingType: SchedulingType = schedulingTypeOrAll;

        return (
            <ModifySchedulingForm
                ref={this.asyncModifySchedulingForm}
                initialSchedulingType={schedulingType}
                initialControlSchedulings={controls.map(
                    (c) => c.control.resource.scheduling ?? null
                )}
                initialStepGroupAllocatedTimes={controls.map((c) => ({
                    initialAllocatedTime:
                        c.control.resource.stepGroup.initialStep.allocatedTime,
                    subsequentAllocatedTimes: {
                        SubsequentStep0:
                            c.control.resource.stepGroup.subsequentSteps[0]
                                .allocatedTime,
                        SubsequentStep1:
                            c.control.resource.stepGroup.subsequentSteps[1]
                                .allocatedTime,
                    },
                }))}
                controlIds={controls.map((c) => c.control.resource.id)}
                calendars={calendars}
                readOnly={readOnly ? "READ_ONLY" : "EDIT"}
                allTags={allTags}
                ajaxManager={ajaxManager}
                financialYear={financialYear}
            />
        );
    }

    private onSubmitScheduleChanges(
        selectedIds: Array<string>,
        onDone: (closeDrawer: boolean) => void
    ) {
        const { ajaxManager } = this.props;
        const schedulingAndAllocatedTimes =
            this.asyncModifySchedulingForm.current?.validate();
        if (!schedulingAndAllocatedTimes) {
            onDone(false);
            return;
        }

        const payload: UpdatableControls = {
            controlIds: selectedIds,
            // It is safer to not use the spread here.
            // Better missing updates than sending incorrect updates.
            scheduling: schedulingAndAllocatedTimes.scheduling,
            allocatedTimes: schedulingAndAllocatedTimes.allocatedTimes,
        };

        const doReload = () => {
            invalidateControlListQueries();
            onDone(true);
            emitNotification("success", t("UserManagement_ControlsUpdated"));
        };

        ajaxManager
            .ajax<void, ControlsStatus>(() => patchControls(payload), {
                component: this,
                initialStatusValue: "DEFAULT",
                inFlightStatus: { type: "SUBMITTING" },
                getSuccessStatus: (_resp) => ({ type: "DEFAULT" }),
                getErrorStatus: (_code) => ({ type: "DEFAULT" }),
                onErrorCode: {
                    460: () => {
                        // So far this is the only error which could possibly lead to 460.
                        emitNotification(
                            "error",
                            t("Recurrence_Validation_Notification_After_Due")
                        );
                    },
                },
            })
            .then(doReload)
            .catch((e) => {
                console.log(e);
                onDone(false);
            });
    }

    private getRowClassName(item: ControlDataRow): string {
        if (item.assignments.length === 0) {
            return theme.disableExpandButton;
        }
        return "";
    }

    private getNameExtendedTableExtractors(): DataExtractor<
        AssignmentRow,
        any,
        UserOrGroup | undefined
    >[] {
        const nameTransformer = (
            userOrGroup: UserOrGroup | undefined
        ): string => {
            if (!userOrGroup) {
                return "";
            }
            switch (userOrGroup.type) {
                case "User":
                    return formatUserName(userOrGroup.value.name);
                case "Group":
                    return userOrGroup.value.name;
                default:
                    assertNever(userOrGroup);
            }
        };

        const valueTransformer = (
            row: AssignmentRow | ReviewerType
        ): UserOrGroup => {
            switch (row.type) {
                case "User":
                    return {
                        type: "User",
                        value: {
                            id: row.userOverview.id,
                            name: {
                                firstname: row.userOverview.firstname || "",
                                lastname: row.userOverview.lastname || "",
                            },
                        },
                    };
                case "Group":
                    return {
                        type: "Group",
                        value: {
                            id: row.userGroup.id,
                            name: row.userGroup.name,
                        },
                    };
                case "Pool":
                    return {
                        type: "Group",
                        value: {
                            id: row.userGroupOverviewResource.userGroup.id,
                            name: row.userGroupOverviewResource.userGroup.name,
                        },
                    };
                default: {
                    assertNever(row);
                }
            }
        };

        const reviewerTransformer = (
            row: AssignmentRow
        ): UserOrGroup | undefined => {
            const firstReviewer = row.firstReviewer || row.finalReviewer;
            if (!firstReviewer) {
                return undefined;
            }
            return valueTransformer(firstReviewer);
        };

        const secondReviewerTransformer = (
            row: AssignmentRow
        ): UserOrGroup | undefined => {
            const finalReviewer = row.firstReviewer
                ? row.finalReviewer
                : undefined;
            if (!finalReviewer) {
                return undefined;
            }
            return valueTransformer(finalReviewer);
        };

        return [
            {
                id: ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
                label: t("ControlResult_Assignee"),
                getValue: (_row) => _row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: valueTransformer,
                    asString: nameTransformer,
                },
            },
            {
                id: ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
                label: t("Collect_ControlProcess_Title_Review"),
                getValue: (row) => row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: reviewerTransformer,
                    asString: nameTransformer,
                },
            },
            {
                id: ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
                label: t("Collect_ControlProcess_Title_SecondaryReview"),
                getValue: (row) => row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: secondReviewerTransformer,
                    asString: nameTransformer,
                },
            },
        ];
    }

    private getEntityNameExtendedTableExtractors(
        entitiesById: Map<number, EntityView>
    ): DataExtractor<
        AssignmentRow,
        any,
        IdAndName<number, string> | undefined
    >[] {
        const entityValueTransformer = (
            row: AssignmentRow
        ): IdAndName<number, string> | undefined => {
            const entityId = row.entityId;
            if (entityId === undefined) {
                return undefined;
            }
            const entity = entitiesById.get(entityId);
            if (!entity) {
                return undefined;
            }
            return { id: entity.id, name: entity.name };
        };

        const entityNameTransformer = (
            idAndName: IdAndName<number, string> | undefined
        ): string => {
            return idAndName?.name ?? "";
        };

        return [
            {
                id: ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY,
                label: t("Control_Management_Entity"),
                getValue: (row) => row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: entityValueTransformer,
                    asString: entityNameTransformer,
                },
            },
        ];
    }

    private getEmailExtendedTableExtractors(): DataExtractor<
        AssignmentRow,
        any,
        string | undefined
    >[] {
        const valueTransformer = (
            row: AssignmentRow | ReviewerType
        ): string | undefined => {
            switch (row.type) {
                case "User":
                    return row.userOverview.email || undefined;
                case "Group":
                case "Pool":
                    return undefined;
                default: {
                    assertNever(row);
                }
            }
        };

        const reviewerTransformer = (
            row: AssignmentRow
        ): string | undefined => {
            const firstReviewer = row.firstReviewer || row.finalReviewer;
            if (!firstReviewer) {
                return undefined;
            }
            return valueTransformer(firstReviewer);
        };

        const secondReviewerTransformer = (
            row: AssignmentRow
        ): string | undefined => {
            const finalReviewer = row.firstReviewer
                ? row.finalReviewer
                : undefined;
            if (!finalReviewer) {
                return undefined;
            }
            return valueTransformer(finalReviewer);
        };
        return [
            {
                id: "onlySearchAssigneeEmail",
                label: t("ControlResult_Assignee"),
                getValue: (_row) => _row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: valueTransformer,
                    asString: (row) => row || "",
                },
            },
            {
                id: "onlySearchFirstReviewerEmail",
                label: t("Collect_ControlProcess_Title_Review"),
                getValue: (row) => row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: reviewerTransformer,
                    asString: (row) => row || "",
                },
            },
            {
                id: "onlySearchSecondReviewerEmail",
                label: t("Collect_ControlProcess_Title_SecondaryReview"),
                getValue: (row) => row,
                compare: () => 0,
                asString: (_row) => "",
                customFilterTransformer: {
                    getValue: secondReviewerTransformer,
                    asString: (row) => row || "",
                },
            },
        ];
    }

    private dataSource = memoizeOne(
        (
            assignments: ControlAssignmentGroup[],
            resources: ControlAssignmentResources,
            extractors: DataExtractor<AssignmentRow, any, any>[]
        ): DataView<AssignmentRow> => {
            const { controlEntitiesEnabled } = this.props;
            return mapPropsToTableDataSource(
                assignments,
                resources,
                controlEntitiesEnabled,
                extractors
            );
        }
    );

    private renderExpandedRowWithFilter: (
        filter: SimpleAndFilter | null,
        allTags: UserTagOverviewListDto,
        controlProgramOverviews: Partial<ControlProgramOverviewResources>
    ) => (item: ControlDataRow, _index: number) => React.ReactNode | undefined =
        memoize(
            (
                filter: SimpleAndFilter | null,
                allTags: UserTagOverviewListDto,
                controlProgramOverviews: Partial<ControlProgramOverviewResources>
            ): ((
                item: ControlDataRow,
                _index: number
            ) => React.ReactNode | undefined) => {
                const entities =
                    controlProgramOverviews.linkedResources?.entities || [];
                const entitiesById = new Map<number, EntityView>(
                    entities.map((e) => [e.id, e])
                );

                return memoize(
                    (
                        item: ControlDataRow /* XXX If you add arguments, please define a memoization strategy */
                    ): React.ReactNode => {
                        const { controlEntitiesEnabled } = this.props;

                        const linkedResources =
                            this.props.controls.linkedResources;
                        if (item.assignments.length === 0) {
                            return undefined;
                        }

                        let allowedExtractorIds: string[] = [
                            ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
                            ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
                            ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
                        ];
                        if (controlEntitiesEnabled) {
                            allowedExtractorIds = [
                                ...allowedExtractorIds,
                                ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY,
                            ];
                        }
                        const assigneeCategoryExtractorsAndExpanders =
                            buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilterInnerTable(
                                allTags,
                                filter
                            );

                        const allExtractors = [
                            ...this.getNameExtendedTableExtractors(),
                            ...this.getEmailExtendedTableExtractors(),
                            ...(controlEntitiesEnabled
                                ? this.getEntityNameExtendedTableExtractors(
                                      entitiesById
                                  )
                                : []),
                            ...assigneeCategoryExtractorsAndExpanders.extractors,
                        ];

                        let dataView = this.dataSource(
                            item.assignments,
                            linkedResources,
                            allExtractors
                        );

                        if (filter) {
                            const localFilters: SimpleAndFilter = {
                                filters: filter.filters.filter(
                                    (simpleOrFilter) =>
                                        allowedExtractorIds.includes(
                                            simpleOrFilter.extractorId
                                        ) ||
                                        simpleOrFilter.extractorId.startsWith(
                                            ASSIGNEE_CATEGORIES_ID
                                        )
                                ),
                            };
                            const index = new DataViewIndex(
                                dataView,
                                allExtractors.map((extractor) => extractor.id),
                                [
                                    ...assigneeCategoryExtractorsAndExpanders.expanders,
                                ]
                            );
                            dataView = index.find(localFilters).dataView;
                        }
                        return (
                            // <div> is needed because of a class which sets margins on the DataTable
                            <div>
                                <DataTable
                                    id="assignments"
                                    columnBuilders={buildColumns(entitiesById)}
                                    dataView={dataView}
                                />
                            </div>
                        );
                    }
                );
            }
        );
}

function fetchFinancialYearId(
    control: ControlWithoutFieldsGraphItemDto
): number | null {
    const scheduling = control.scheduling;
    if (!scheduling) {
        return null;
    }
    const rule = scheduling.rule;
    switch (rule.type) {
        case "Date":
        case "Weekly":
        case "Monthly":
        case "EveryTwoMonths":
            return null;
        case "Quarterly":
            return rule.financialYearId ?? null;
        case "HalfYearly":
            return rule.financialYearId ?? null;
        case "Yearly":
            return rule.financialYearId ?? null;
        default:
            return assertNever(rule);
    }
}

function fetchCalendarId(
    control: ControlWithoutFieldsGraphItemDto
): number | null {
    const scheduling = control.scheduling;
    if (!scheduling) {
        return null;
    }
    const rule = scheduling.rule;
    switch (rule.type) {
        case "Date":
        case "Weekly":
            return null;
        case "Monthly":
            return rule.calendarId ?? null;
        case "EveryTwoMonths":
            return rule.calendarId ?? null;
        case "Quarterly":
            return rule.calendarId ?? null;
        case "HalfYearly":
            return rule.calendarId ?? null;
        case "Yearly":
            return rule.calendarId ?? null;
        default:
            return assertNever(rule);
    }
}

function schedulingTypeOrAllToPageKey(
    schedulingTypeOrAll: SchedulingTypeOrAll
): PageKeyControls {
    switch (schedulingTypeOrAll) {
        case "All":
            return PAGE_KEY.controlsAll();
        case "Weekly":
            return PAGE_KEY.controlsWeekly();
        case "Monthly":
            return PAGE_KEY.controlsMonthly();
        case "EveryTwoMonths":
            return PAGE_KEY.controlsTwoMonths();
        case "Quarterly":
            return PAGE_KEY.controlsQuarterly();
        case "HalfYearly":
            return PAGE_KEY.controlsHalfYearly();
        case "Yearly":
            return PAGE_KEY.controlsYearly();
        case "Manual":
            return PAGE_KEY.controlsManual();
        default:
            return assertNever(schedulingTypeOrAll);
    }
}

interface AdditionalExportExpandoData {
    assignment?: ExportedAssignmentRow;
}

function mapBaseItemExportedColumnToExpandoColumn(
    column: SimpleColumn<ControlDataRow>
): SimpleColumn<DV.Expando<ControlDataRow, AdditionalExportExpandoData>> {
    return {
        ...column,
        extractor: {
            id: column.extractor.id,
            label: column.extractor.label,
            getValue: (row) => column.extractor.getValue(row.baseItem),
            compare: (a, b) => column.extractor.compare(a.baseItem, b.baseItem),
            asString: (row) => column.extractor.asString(row.baseItem),
        },
        getFormattedValue: (row) => column.getFormattedValue(row.baseItem),
        getCopyableValue: (row) => column.getCopyableValue(row.baseItem),
        cellClassName: undefined,
    };
}

/**
 * In order to reuse existing extractors for AssignmentRow's, we want to transform them into expanded rows extractors.
 */
function mapAssignmentRowColumnToExpandoColumn(
    column: SimpleColumn<ExportedAssignmentRow>
): SimpleColumn<DV.Expando<ControlDataRow, AdditionalExportExpandoData>> {
    return {
        ...column,
        extractor: {
            id: column.extractor.id,
            label: column.extractor.label,
            getValue: (row) => {
                if (row.additionalData.assignment !== undefined) {
                    return column.extractor.getValue(
                        row.additionalData.assignment
                    );
                }
                return undefined;
            },
            compare: (a, b) => {
                if (
                    a.additionalData.assignment !== undefined &&
                    b.additionalData.assignment !== undefined
                ) {
                    return column.extractor.compare(
                        a.additionalData.assignment,
                        b.additionalData.assignment
                    );
                }
                return Cmp.map((row) => row, Cmp.undefinedFirst())(a, b);
            },
            asString: (row) => {
                if (row.additionalData.assignment !== undefined) {
                    return column.extractor.asString(
                        row.additionalData.assignment
                    );
                }
                return "";
            },
        },
        getFormattedValue: (row) => {
            if (row.additionalData.assignment !== undefined) {
                return column.getFormattedValue(row.additionalData.assignment);
            }
            return undefined;
        },
        getCopyableValue: (row) => {
            if (row.additionalData.assignment !== undefined) {
                return column.getCopyableValue(row.additionalData.assignment);
            }
            return "";
        },
        cellClassName: undefined,
    } satisfies SimpleColumn<
        DV.Expando<ControlDataRow, AdditionalExportExpandoData>
    >;
}

function expandAssignmentRowIntoExportedAssignmentRows(
    row: AssignmentRow
): ExportedAssignmentRow[] {
    switch (row.type) {
        case "Group": {
            const members = row.userGroup.userGroupUsers;
            return members.map((member) => {
                return {
                    type: "GroupIndividual",
                    userGroup: row.userGroup,
                    userOverview: member,
                    id: row.id + "_" + member.id,
                    tagIds: row.tagIds,
                    firstReviewer: row.firstReviewer,
                    finalReviewer: row.finalReviewer,
                    notify: row.notify,
                    entityId: row.entityId,
                } satisfies AssignmentRowForUserGroupIndividual;
            });
        }
        case "User":
        case "Pool":
            return [row];
        default:
            assertNever(row);
    }
}

function invalidateControlListQueries() {
    return Promise.all(
        [
            getGetControlsQueryKey(),
            getGetControlsResourceCountQueryKey(),
            getGetTagsQueryKey(),
            getGetFinancialYearQueryKey(),
            getGetCalendarsQueryKey(),
            getGetControlProgramsQueryKey(),
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey }))
    );
}

function controlViewMetaData(schedulingTypeOrAll: SchedulingTypeOrAll): {
    page: ControlViewPage;
    title: string;
    breadcrumbs: Array<{ label: string; url: string }>;
} {
    const controlsBreadcrumb = {
        label: t("RiskMapResponse_Columns_Controls"),
        url: WebRoute.CONTROLS_ALL,
    };

    switch (schedulingTypeOrAll) {
        case "All":
            return {
                page: PAGE_CONTROLS_ALL,
                title: t("RiskMapResponse_Columns_Controls"),
                breadcrumbs: [],
            };
        case "Weekly":
            return {
                page: PAGE_CONTROLS_WEEKLY,
                title: t("CreateControl_Recurrence_Interval_Weekly"),
                breadcrumbs: [controlsBreadcrumb],
            };
        case "Monthly":
            return {
                page: PAGE_CONTROLS_MONTHLY,
                title: t("CreateControl_Recurrence_Interval_Monthly"),
                breadcrumbs: [controlsBreadcrumb],
            };
        case "EveryTwoMonths":
            return {
                page: PAGE_CONTROLS_TWO_MONTHS,
                title: t("CreateControl_Recurrence_Interval_Every2Months"),
                breadcrumbs: [controlsBreadcrumb],
            };
        case "Quarterly":
            return {
                page: PAGE_CONTROLS_QUARTERLY,
                title: t("CreateControl_Recurrence_Interval_Quarterly"),
                breadcrumbs: [controlsBreadcrumb],
            };
        case "HalfYearly":
            return {
                page: PAGE_CONTROLS_HALF_YEARLY,
                title: t("CreateControl_Recurrence_Interval_HalfYearly"),
                breadcrumbs: [controlsBreadcrumb],
            };
        case "Yearly":
            return {
                page: PAGE_CONTROLS_YEARLY,
                title: t("CreateControl_Recurrence_Interval_Yearly"),
                breadcrumbs: [controlsBreadcrumb],
            };
        case "Manual":
            return {
                page: PAGE_CONTROLS_MANUAL,
                title: t("CreateControl_Recurrence_Manual"),
                breadcrumbs: [controlsBreadcrumb],
            };
        default:
            return assertNever(schedulingTypeOrAll);
    }
}

/**
 * Transforms label (for example by localizing it).
 * Used in controls export to Excel.
 */
interface ExportLabelTransformer {
    /**
     * Checks whether column matches the transformer filter.
     * If yes, the transformer can operate on the label.
     */
    check: (extractorId: string) => boolean;
    /**
     * Transforms label
     */
    transform: (label: string) => string;
}

export {
    ControlsWithFeatureFlag as Controls,
    type InnerControlsProps as ControlProps,
};
